import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/form";
import AuthShell from "./AuthShell";
import PasswordInput from "./PasswordInput";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});

/** Coach login at /login, client login at /client (same accounts, different wording). */
export default function LoginPage({ audience = "coach" }) {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { email: "", password: "" } });

  const from = location.state?.from || "";
  const nextFor = (role) => (role === "client" ? (from.startsWith("/me") ? from : "/me") : from.startsWith("/app") ? from : "/app");
  if (user) return <Navigate to={nextFor(user.role)} replace />;

  const onSubmit = async ({ email, password }) => {
    setServerError("");
    try {
      const data = await login(email, password);
      navigate(nextFor(data?.user?.role), { replace: true });
    } catch (e) {
      setServerError(formatApiError(e));
    }
  };

  return (
    <AuthShell
      variant={audience}
      title={audience === "client" ? "Log in to your training" : "Welcome back, coach"}
      subtitle={
        audience === "client"
          ? "Your workouts, meals and check-ins from your coach."
          : "Log in to run your coaching business."
      }
      footer={
        audience === "client" ? (
          <div className="flex flex-col gap-3">
            <p className="rounded-xl bg-soft px-3.5 py-3 leading-6">
              <span className="font-medium text-ink">First time?</span> Your coach sends you an invite link by email or text. Open it to create your
              password, then log in here.
            </p>
            <span>
              Are you a coach?{" "}
              <Link to="/login" className="font-medium text-ink underline underline-offset-4">
                Coach login
              </Link>
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <span>
              New to Fitamingo?{" "}
              <Link to="/signup" className="font-medium text-ink underline underline-offset-4">
                Start for free
              </Link>
            </span>
            <span>
              Training with a coach?{" "}
              <Link to="/client" className="font-medium text-ink underline underline-offset-4">
                Client login
              </Link>
            </span>
          </div>
        )
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <Field label="Email" error={errors.email?.message}>
          {(id, err) => (
            <Input id={id} type="email" autoComplete="email" aria-invalid={!!errors.email} aria-describedby={err} {...register("email")} />
          )}
        </Field>
        <Field label="Password" error={errors.password?.message}>
          {(id, err) => (
            <PasswordInput id={id} autoComplete="current-password" aria-invalid={!!errors.password} aria-describedby={err} {...register("password")} />
          )}
        </Field>

        {serverError && (
          <p role="alert" className="rounded-[10px] border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
            {serverError}
          </p>
        )}

        <Button type="submit" size="lg" disabled={isSubmitting} className="mt-1 w-full">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Log in
        </Button>
      </form>
    </AuthShell>
  );
}
